import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Trash2,
  Package,
  FileText,
  Users,
  Leaf,
  Star,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import type { Database } from '../../lib/supabase'
import { supabase } from '../../lib/supabase'
import { useCreateArticle, useUpdateArticle, useDeleteArticle, useArticlePricesAcrossLists, useSetArticlePriceInList, useArticleCategories } from '../../hooks/useSupabase'
import { priceCalculations } from '../../services/priceLists'

type Article = Database['public']['Tables']['articles']['Row']
type ArticleInsert = Database['public']['Tables']['articles']['Insert']

// Swedish VAT rates
const vatRates = [
  { value: 25, label: '25%', description: 'Standard' },
  { value: 12, label: '12%', description: 'Livsmedel, hotell' },
  { value: 6, label: '6%', description: 'Böcker, kultur' },
  { value: 0, label: '0%', description: 'Momsfritt' }
]

// Common units
const units = [
  { value: 'st', label: 'st', description: 'Styck' },
  { value: 'timme', label: 'timme', description: 'Timmar' },
  { value: 'm²', label: 'm²', description: 'Kvadratmeter' },
  { value: 'm', label: 'm', description: 'Meter' },
  { value: 'kg', label: 'kg', description: 'Kilogram' },
  { value: 'liter', label: 'liter', description: 'Liter' },
  { value: 'paket', label: 'paket', description: 'Paket' }
]

// Article types
const articleTypes = [
  { value: 'service', label: 'Tjänst', description: 'Engångstjänst' },
  { value: 'hourly', label: 'Timtjänst', description: 'Timbaserad tjänst' },
  { value: 'product', label: 'Produkt', description: 'Fysisk produkt' }
]

export function ArticleDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNewArticle = id === 'new'
  const [loading, setLoading] = useState(!isNewArticle)
  const [saving, setSaving] = useState(false)
  const [article, setArticle] = useState<Article | null>(null)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const createArticle = useCreateArticle()
  const updateArticle = useUpdateArticle()
  const deleteArticle = useDeleteArticle()

  // Navigation between filtered articles
  const [filteredArticleIds, setFilteredArticleIds] = useState<string[]>([])

  // Load filtered article IDs from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('filtered-article-ids')
      if (saved) {
        setFilteredArticleIds(JSON.parse(saved))
      }
    } catch (e) {
      console.error('Error loading filtered article IDs:', e)
    }
  }, [])

  // Calculate navigation state (with wrap-around)
  const currentIndex = id && !isNewArticle ? filteredArticleIds.indexOf(id) : -1
  const canNavigate = filteredArticleIds.length > 1 && currentIndex >= 0
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : filteredArticleIds.length - 1
  const nextIndex = currentIndex < filteredArticleIds.length - 1 ? currentIndex + 1 : 0

  // Reset lastSaved when navigating to a different article
  useEffect(() => {
    setLastSaved(null)
  }, [id])

  // Collapsible section states
  const [basicInfoCollapsed, setBasicInfoCollapsed] = useState(false)
  const [priceListsCollapsed, setPriceListsCollapsed] = useState(true)

  // Track how many assignments use this article
  const [assignmentCount, setAssignmentCount] = useState(0)

  // Load assignment count for this article
  useEffect(() => {
    if (!id || isNewArticle) return

    const loadAssignmentCount = async () => {
      const { count, error } = await supabase
        .from('assignment_articles')
        .select('*', { count: 'exact', head: true })
        .eq('article_id', id)

      if (!error && count !== null) {
        setAssignmentCount(count)
      }
    }

    loadAssignmentCount()
  }, [id, isNewArticle])

  // Price list hooks
  const { data: articlePrices = [], isLoading: loadingPrices } = useArticlePricesAcrossLists(id || '')
  const setArticlePriceMutation = useSetArticlePriceInList()

  // Categories hook
  const { data: categories = [] } = useArticleCategories()

  // Local state for price editing - stores both ex and incl values
  const [editingPrices, setEditingPrices] = useState<Map<string, { exclVat: string; inclVat: string; lastEdited: 'excl' | 'incl' }>>(new Map())

  // Track original data for unsaved changes detection
  const [originalFormData, setOriginalFormData] = useState<typeof formData | null>(null)

  const [formData, setFormData] = useState<ArticleInsert>({
    name: '',
    description: '',
    category: '',
    is_rut_applicable: false,
    is_provision_article: false,
    vat_rate: 25,
    unit: 'st',
    type: 'service'
  })

  // Check for unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!originalFormData) return false
    return JSON.stringify(formData) !== JSON.stringify(originalFormData)
  }

  // Warn before leaving with unsaved changes (browser navigation/close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  // Navigate to previous article (with wrap-around)
  const navigateToPrevious = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/articles/${filteredArticleIds[previousIndex]}`)
  }

  // Navigate to next article (with wrap-around)
  const navigateToNext = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/articles/${filteredArticleIds[nextIndex]}`)
  }

  // Keyboard navigation with Page Up/Down
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return
      }
      if (e.key === 'PageUp') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'PageDown') {
        e.preventDefault()
        navigateToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canNavigate, filteredArticleIds, previousIndex, nextIndex])

  // Handle back/cancel button with unsaved changes confirmation
  const handleBack = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        navigate('/admin/articles')
      }
    } else {
      navigate('/admin/articles')
    }
  }

  // Load article data (only if not new article)
  useEffect(() => {
    if (!id) {
      navigate('/admin/articles')
      return
    }

    // If it's a new article, don't load anything
    if (isNewArticle) {
      setLoading(false)
      const initialFormData: ArticleInsert = {
        name: '',
        description: '',
        category: '',
        is_rut_applicable: false,
        is_provision_article: false,
        vat_rate: 25,
        unit: 'st',
        type: 'service' as const
      }
      setFormData(initialFormData)
      setOriginalFormData(initialFormData)
      return
    }

    const loadArticle = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('articles')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          console.error('Error loading article:', error)
          toast.error('Kunde inte ladda artikel: ' + error.message)
          navigate('/admin/articles')
          return
        }

        if (!data) {
          toast.error('Artikel hittades inte')
          navigate('/admin/articles')
          return
        }

        setArticle(data)
        const loadedFormData: ArticleInsert = {
          name: data.name,
          description: data.description || '',
          category: data.category || '',
          is_rut_applicable: data.is_rut_applicable,
          is_provision_article: data.is_provision_article || false,
          vat_rate: data.vat_rate || 25,
          unit: data.unit || 'st',
          type: (data.type || 'service') as 'product' | 'service' | 'hourly'
        }
        setFormData(loadedFormData)
        setOriginalFormData(loadedFormData)
      } catch (error) {
        console.error('Error loading article:', error)
        toast.error('Kunde inte ladda artikel')
        navigate('/admin/articles')
      } finally {
        setLoading(false)
      }
    }

    loadArticle()
  }, [id, navigate, isNewArticle])

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Ingen kategori'
    const category = categories.find(c => c.id === categoryId)
    return category?.name || 'Okänd kategori'
  }

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#6B7280'
    const category = categories.find(c => c.id === categoryId)
    return category?.color || '#6B7280'
  }

  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Namn och kategori är obligatoriska')
      return
    }

    try {
      setSaving(true)

      if (isNewArticle) {
        // Create new article
        const result = await createArticle.mutateAsync(formData)
        toast.success('Artikel skapad framgångsrikt')
        // Navigate to the new article's detail page
        if (result?.id) {
          navigate(`/admin/articles/${result.id}`)
        }
      } else {
        // Update existing article
        if (!id) return
        await updateArticle.mutateAsync({ id, updates: formData })
        toast.success('Artikel uppdaterad framgångsrikt')
        setLastSaved(new Date())
        setOriginalFormData({ ...formData })
        // Reload article data
        const { data } = await supabase
          .from('articles')
          .select('*')
          .eq('id', id)
          .single()

        if (data) {
          setArticle(data)
        }
      }
    } catch (error) {
      console.error('Error saving article:', error)
      toast.error('Kunde inte spara artikel')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (isNewArticle || !id) return
    if (!confirm('Är du säker på att du vill ta bort denna artikel?')) {
      return
    }

    try {
      await deleteArticle.mutateAsync(id)
      toast.success('Artikel borttagen')
      navigate('/admin/articles')
    } catch (error) {
      console.error('Error deleting article:', error)
      toast.error('Kunde inte ta bort artikel')
    }
  }

  // Handle price change - ex moms
  const handlePriceExclChange = (priceListId: string, value: string, currentVatRate: number) => {
    const numValue = parseFloat(value) || 0
    const inclVat = numValue > 0 ? priceCalculations.calculatePriceInclVat(numValue, currentVatRate).toFixed(2) : ''

    setEditingPrices(prev => {
      const newMap = new Map(prev)
      newMap.set(priceListId, { exclVat: value, inclVat, lastEdited: 'excl' })
      return newMap
    })
  }

  // Handle price change - ink moms
  const handlePriceInclChange = (priceListId: string, value: string, currentVatRate: number) => {
    const numValue = parseFloat(value) || 0
    const exclVat = numValue > 0 ? priceCalculations.calculatePriceExclVat(numValue, currentVatRate).toFixed(2) : ''

    setEditingPrices(prev => {
      const newMap = new Map(prev)
      newMap.set(priceListId, { exclVat, inclVat: value, lastEdited: 'incl' })
      return newMap
    })
  }

  // Save price for a specific price list
  const handleSavePrice = async (priceListId: string) => {
    if (!id) return

    const editState = editingPrices.get(priceListId)
    const price = editState?.exclVat ? parseFloat(editState.exclVat) : null

    try {
      await setArticlePriceMutation.mutateAsync({
        priceListId,
        articleId: id,
        priceExclVat: price
      })
      toast.success('Pris sparat')
      // Clear editing state for this price list
      setEditingPrices(prev => {
        const newMap = new Map(prev)
        newMap.delete(priceListId)
        return newMap
      })
    } catch (error) {
      console.error('Error saving price:', error)
      toast.error('Kunde inte spara pris')
    }
  }

  // Get the display values for price inputs
  const getPriceValues = (priceListId: string, currentPrice: number | null, currentVatRate: number): { exclVat: string; inclVat: string } => {
    if (editingPrices.has(priceListId)) {
      const state = editingPrices.get(priceListId)!
      return { exclVat: state.exclVat, inclVat: state.inclVat }
    }
    if (currentPrice && currentPrice > 0) {
      return {
        exclVat: currentPrice.toString(),
        inclVat: priceCalculations.calculatePriceInclVat(currentPrice, currentVatRate).toFixed(2)
      }
    }
    return { exclVat: '', inclVat: '' }
  }

  // Check if price has been edited
  const isPriceEdited = (priceListId: string, currentPrice: number | null): boolean => {
    if (!editingPrices.has(priceListId)) return false
    const editState = editingPrices.get(priceListId)!
    const currentValue = currentPrice?.toString() || ''
    return editState.exclVat !== currentValue
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar artikel...</p>
        </div>
      </div>
    )
  }

  // Only return null if it's not a new article and we don't have article data
  if (!isNewArticle && !article) {
    return null
  }

  // Use formData for vatRate since it's always up-to-date
  const vatRate = formData.vat_rate || 25

  return (
    <div className="space-y-6">
      {/* Header Ribbon */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {!isNewArticle && article && (
                <span className="text-sm font-mono px-2 py-0.5 bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400 rounded">
                  {article.article_number}
                </span>
              )}
              <h1 className="text-lg sm:text-xl font-bold text-nature-900 dark:text-nature-100 truncate">
                {isNewArticle ? 'Ny artikel' : (formData.name || 'Namnlös artikel')}
              </h1>
              {!isNewArticle && (
                <>
                  {/* Category badge */}
                  {formData.category && (
                    <span
                      className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                      style={{ backgroundColor: getCategoryColor(formData.category) }}
                    >
                      {getCategoryName(formData.category)}
                    </span>
                  )}
                  {/* Type badge */}
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 font-medium">
                    {articleTypes.find(t => t.value === (formData.type || 'service'))?.label || 'Tjänst'}
                  </span>
                </>
              )}
            </div>
            {isNewArticle && (
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-0.5">
                Fyll i uppgifterna nedan
              </p>
            )}
            {/* Default prices display */}
            {!isNewArticle && articlePrices.length > 0 && (
              <div className="flex items-center gap-4 mt-1">
                {(() => {
                  const defaultCustomerPrice = articlePrices.find(ap => ap.priceList.type === 'customer' && ap.priceList.is_default)
                  const defaultSubcontractorPrice = articlePrices.find(ap => ap.priceList.type === 'subcontractor' && ap.priceList.is_default)
                  return (
                    <>
                      {defaultCustomerPrice && defaultCustomerPrice.price !== null && (
                        <div className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
                            {priceCalculations.formatPrice(defaultCustomerPrice.price)}
                          </span>
                        </div>
                      )}
                      {defaultSubcontractorPrice && defaultSubcontractorPrice.price !== null && (
                        <div className="flex items-center gap-1.5">
                          <Leaf className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
                            {priceCalculations.formatPrice(defaultSubcontractorPrice.price)}
                          </span>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            {/* Navigation between filtered articles (with wrap-around) */}
            {!isNewArticle && filteredArticleIds.length > 1 && (
              <div className="flex items-center gap-1 border-r border-nature-200 dark:border-nature-600 pr-2 mr-1">
                <button
                  onClick={navigateToPrevious}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Föregående artikel (Page Up)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-xs text-nature-500 dark:text-nature-400 min-w-[3rem] text-center">
                  {currentIndex + 1} / {filteredArticleIds.length}
                </span>
                <button
                  onClick={navigateToNext}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Nästa artikel (Page Down)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}

            {lastSaved && !hasUnsavedChanges() && (
              <span className="text-xs text-nature-500 dark:text-nature-400">
                Sparad {format(lastSaved, 'HH:mm')}
              </span>
            )}

            {/* Action buttons - only for existing articles */}
            {!isNewArticle && (
              <div className="flex items-center gap-1">
                <button
                  onClick={assignmentCount === 0 ? handleDelete : undefined}
                  disabled={assignmentCount > 0}
                  title={assignmentCount > 0
                    ? `Kan inte ta bort - artikeln används i ${assignmentCount} uppdrag`
                    : 'Ta bort artikel'
                  }
                  className={`p-2 rounded-lg transition-colors ${
                    assignmentCount > 0
                      ? 'text-nature-400 dark:text-nature-500 cursor-not-allowed'
                      : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}

            <button
              onClick={handleBack}
              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors w-full sm:w-auto ${
                hasUnsavedChanges()
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700'
                  : 'btn-outline'
              }`}
            >
              {hasUnsavedChanges() ? (
                <>
                  <X className="h-4 w-4" />
                  <span>Avbryt</span>
                </>
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  <span>Tillbaka</span>
                </>
              )}
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Sparar...' : 'Spara'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 1. Grundinformation - Expanded by default */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <button
          onClick={() => setBasicInfoCollapsed(!basicInfoCollapsed)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Grundinformation</h3>
          </div>
          {basicInfoCollapsed ? (
            <ChevronDown className="h-5 w-5 text-nature-500" />
          ) : (
            <ChevronUp className="h-5 w-5 text-nature-500" />
          )}
        </button>
        {!basicInfoCollapsed && (
          <div className="px-6 pb-6 space-y-4 border-t border-nature-200 dark:border-nature-700 pt-4">
            <div>
              <label className="form-label">Namn *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-field"
                placeholder="Artikelnamn"
              />
            </div>

            <div>
              <label className="form-label">Beskrivning</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field"
                rows={3}
                placeholder="Beskrivning av artikeln/tjänsten"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Kategori *</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                >
                  <option value="">Välj kategori</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Typ *</label>
                <select
                  value={formData.type || 'service'}
                  onChange={(e) => {
                    const newType = e.target.value as 'product' | 'service' | 'hourly'
                    setFormData({
                      ...formData,
                      type: newType,
                      // Auto-set unit when changing type
                      unit: newType === 'hourly' ? 'timme' : (formData.unit === 'timme' ? 'st' : formData.unit)
                    })
                  }}
                  className="input-field"
                >
                  {articleTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Enhet *</label>
                <select
                  value={formData.unit || 'st'}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="input-field"
                >
                  {units.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label} ({unit.description})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Momssats *</label>
                <select
                  value={formData.vat_rate ?? 25}
                  onChange={(e) => setFormData({ ...formData, vat_rate: parseInt(e.target.value) })}
                  className="input-field"
                >
                  {vatRates.map((rate) => (
                    <option key={rate.value} value={rate.value}>
                      {rate.label} ({rate.description})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isRutApplicable"
                  checked={formData.is_rut_applicable}
                  onChange={(e) => setFormData({ ...formData, is_rut_applicable: e.target.checked })}
                  className="h-4 w-4 text-nature-600 focus:ring-nature-500 border-nature-300 rounded"
                />
                <label htmlFor="isRutApplicable" className="ml-2 text-sm font-medium text-nature-900 dark:text-nature-100">
                  RUT-applicerbar
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isProvisionArticle"
                  checked={formData.is_provision_article}
                  onChange={(e) => setFormData({ ...formData, is_provision_article: e.target.checked })}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-nature-300 rounded"
                />
                <label htmlFor="isProvisionArticle" className="ml-2 text-sm font-medium text-nature-900 dark:text-nature-100">
                  Provisionsartikel
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2. Prislistor - Collapsed by default, only for existing articles */}
      {!isNewArticle && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
          <button
            onClick={() => setPriceListsCollapsed(!priceListsCollapsed)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-nature-600 dark:text-nature-400" />
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Prislistor</h3>
              <span className="text-sm text-nature-500 dark:text-nature-400">
                ({articlePrices.length} prislist{articlePrices.length === 1 ? 'a' : 'or'})
              </span>
            </div>
            {priceListsCollapsed ? (
              <ChevronDown className="h-5 w-5 text-nature-500" />
            ) : (
              <ChevronUp className="h-5 w-5 text-nature-500" />
            )}
          </button>
          {!priceListsCollapsed && (
            <div className="px-6 pb-6 border-t border-nature-200 dark:border-nature-700 pt-4">
              <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
                Sätt pris för denna artikel i olika prislistor. Moms ({vatRate}%) beräknas automatiskt.
              </p>

              {loadingPrices ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nature-600"></div>
                </div>
              ) : articlePrices.length === 0 ? (
                <div className="text-center py-8 text-nature-500 dark:text-nature-400">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Inga aktiva prislistor hittades</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Customer Price Lists */}
                  {articlePrices.filter(ap => ap.priceList.type === 'customer').length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4 text-blue-500" />
                        Kundprislistor
                      </h4>
                      <div className="space-y-2">
                        {articlePrices
                          .filter(ap => ap.priceList.type === 'customer')
                          .map(({ priceList, price }) => {
                            const priceValues = getPriceValues(priceList.id, price, vatRate)
                            const numericExcl = parseFloat(priceValues.exclVat) || 0
                            const isEdited = isPriceEdited(priceList.id, price)

                            return (
                              <div
                                key={priceList.id}
                                className="p-3 sm:p-4 bg-nature-50 dark:bg-nature-700/30 rounded-lg"
                              >
                                {/* Header: Name + default star */}
                                <div className="flex items-center justify-between mb-2 sm:mb-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-nature-900 dark:text-nature-100">
                                      {priceList.name}
                                    </span>
                                    {priceList.is_default && (
                                      <Star className="h-4 w-4 text-amber-500 fill-current" />
                                    )}
                                  </div>
                                  {/* Save button - visible on mobile in header */}
                                  <button
                                    onClick={() => handleSavePrice(priceList.id)}
                                    disabled={!isEdited || setArticlePriceMutation.isPending}
                                    className={`sm:hidden px-3 py-1 text-sm rounded-lg transition-colors ${
                                      isEdited
                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                        : 'bg-nature-200 dark:bg-nature-600 text-nature-400 dark:text-nature-500 cursor-not-allowed'
                                    }`}
                                  >
                                    Spara
                                  </button>
                                </div>
                                {priceList.description && (
                                  <p className="text-xs text-nature-500 dark:text-nature-400 mb-2">
                                    {priceList.description}
                                  </p>
                                )}

                                {/* Price inputs - stacked on mobile, row on desktop */}
                                <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 sm:gap-3">
                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Ex moms
                                    </div>
                                    <input
                                      type="number"
                                      value={priceValues.exclVat}
                                      onChange={(e) => handlePriceExclChange(priceList.id, e.target.value, vatRate)}
                                      className="w-full sm:w-24 text-right input-field py-1 px-2 text-sm"
                                      placeholder="0"
                                      min="0"
                                      step="0.01"
                                    />
                                  </div>

                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Moms ({vatRate}%)
                                    </div>
                                    <div className="text-sm text-nature-600 dark:text-nature-400 py-1">
                                      {numericExcl > 0 ? priceCalculations.formatPrice(priceCalculations.calculateVatAmount(numericExcl, vatRate)) : '-'}
                                    </div>
                                  </div>

                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Ink moms
                                    </div>
                                    <input
                                      type="number"
                                      value={priceValues.inclVat}
                                      onChange={(e) => handlePriceInclChange(priceList.id, e.target.value, vatRate)}
                                      className="w-full sm:w-24 text-right input-field py-1 px-2 text-sm"
                                      placeholder="0"
                                      min="0"
                                      step="0.01"
                                    />
                                  </div>

                                  {/* Save button - desktop only (mobile is in header) */}
                                  <button
                                    onClick={() => handleSavePrice(priceList.id)}
                                    disabled={!isEdited || setArticlePriceMutation.isPending}
                                    className={`hidden sm:block px-3 py-1 text-sm rounded-lg transition-colors ${
                                      isEdited
                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                        : 'bg-nature-200 dark:bg-nature-600 text-nature-400 dark:text-nature-500 cursor-not-allowed'
                                    }`}
                                  >
                                    Spara
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )}

                  {/* Subcontractor Price Lists */}
                  {articlePrices.filter(ap => ap.priceList.type === 'subcontractor').length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-3 flex items-center gap-2">
                        <Leaf className="h-4 w-4 text-green-500" />
                        Trädgårdsmästarprislistor
                      </h4>
                      <div className="space-y-2">
                        {articlePrices
                          .filter(ap => ap.priceList.type === 'subcontractor')
                          .map(({ priceList, price }) => {
                            const priceValues = getPriceValues(priceList.id, price, vatRate)
                            const numericExcl = parseFloat(priceValues.exclVat) || 0
                            const isEdited = isPriceEdited(priceList.id, price)

                            return (
                              <div
                                key={priceList.id}
                                className="p-3 sm:p-4 bg-nature-50 dark:bg-nature-700/30 rounded-lg"
                              >
                                {/* Header: Name + default star */}
                                <div className="flex items-center justify-between mb-2 sm:mb-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-nature-900 dark:text-nature-100">
                                      {priceList.name}
                                    </span>
                                    {priceList.is_default && (
                                      <Star className="h-4 w-4 text-amber-500 fill-current" />
                                    )}
                                  </div>
                                  {/* Save button - visible on mobile in header */}
                                  <button
                                    onClick={() => handleSavePrice(priceList.id)}
                                    disabled={!isEdited || setArticlePriceMutation.isPending}
                                    className={`sm:hidden px-3 py-1 text-sm rounded-lg transition-colors ${
                                      isEdited
                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                        : 'bg-nature-200 dark:bg-nature-600 text-nature-400 dark:text-nature-500 cursor-not-allowed'
                                    }`}
                                  >
                                    Spara
                                  </button>
                                </div>
                                {priceList.description && (
                                  <p className="text-xs text-nature-500 dark:text-nature-400 mb-2">
                                    {priceList.description}
                                  </p>
                                )}

                                {/* Price inputs - stacked on mobile, row on desktop */}
                                <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 sm:gap-3">
                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Ex moms
                                    </div>
                                    <input
                                      type="number"
                                      value={priceValues.exclVat}
                                      onChange={(e) => handlePriceExclChange(priceList.id, e.target.value, vatRate)}
                                      className="w-full sm:w-24 text-right input-field py-1 px-2 text-sm"
                                      placeholder="0"
                                      min="0"
                                      step="0.01"
                                    />
                                  </div>

                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Moms ({vatRate}%)
                                    </div>
                                    <div className="text-sm text-nature-600 dark:text-nature-400 py-1">
                                      {numericExcl > 0 ? priceCalculations.formatPrice(priceCalculations.calculateVatAmount(numericExcl, vatRate)) : '-'}
                                    </div>
                                  </div>

                                  <div>
                                    <div className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                      Ink moms
                                    </div>
                                    <input
                                      type="number"
                                      value={priceValues.inclVat}
                                      onChange={(e) => handlePriceInclChange(priceList.id, e.target.value, vatRate)}
                                      className="w-full sm:w-24 text-right input-field py-1 px-2 text-sm"
                                      placeholder="0"
                                      min="0"
                                      step="0.01"
                                    />
                                  </div>

                                  {/* Save button - desktop only (mobile is in header) */}
                                  <button
                                    onClick={() => handleSavePrice(priceList.id)}
                                    disabled={!isEdited || setArticlePriceMutation.isPending}
                                    className={`hidden sm:block px-3 py-1 text-sm rounded-lg transition-colors ${
                                      isEdited
                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                        : 'bg-nature-200 dark:bg-nature-600 text-nature-400 dark:text-nature-500 cursor-not-allowed'
                                    }`}
                                  >
                                    Spara
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  )
}
